/**
 * UAE emirates and their delivery-eligibility groups (ops sheet, Oct 2026).
 * Pure module, no React — imported by both the AeDeliveryEta components and
 * components/ring-air/utils/types.ts (RingContextType), so it must stay free
 * of React/context imports to avoid dependency cycles.
 *
 * Array order is the render order of the emirate <select> options.
 */
export const AE_EMIRATES = [
  'dubai',
  'abu_dhabi',
  'sharjah',
  'ajman',
  'ras_al_khaimah',
  'fujairah',
  'umm_al_quwain',
] as const;

export type AeEmirate = (typeof AE_EMIRATES)[number];

/** Delivery groups: dubai and abu_dhabi_sharjah carry the fast-delivery
 * promise (different 4-hour cutoffs); ineligible emirates fall back to the
 * standard shipping estimate. */
export type AeEmirateGroup = 'dubai' | 'abu_dhabi_sharjah' | 'ineligible';

const EMIRATE_GROUPS: Record<AeEmirate, AeEmirateGroup> = {
  dubai: 'dubai',
  abu_dhabi: 'abu_dhabi_sharjah',
  sharjah: 'abu_dhabi_sharjah',
  ajman: 'ineligible',
  ras_al_khaimah: 'ineligible',
  fujairah: 'ineligible',
  umm_al_quwain: 'ineligible',
};

/** `null` (no emirate chosen yet) deliberately resolves to 'dubai' — before
 * a selection every surface shows the best-case time-based window copy. */
export const aeEmirateGroupOf = (emirate: AeEmirate | null): AeEmirateGroup =>
  emirate === null ? 'dubai' : EMIRATE_GROUPS[emirate];

/** Validates a raw string (sessionStorage, <select> value) back into the
 * union; anything unrecognized becomes null rather than a bogus member. */
export const parseAeEmirate = (value: string | null): AeEmirate | null =>
  (AE_EMIRATES as readonly string[]).includes(value ?? '')
    ? (value as AeEmirate)
    : null;
